import { useClient } from '@based/sdk'
import { useEffect, useRef } from 'react'
import type { BasedChannel } from '@based/sdk'
import Fingerprint, { FingerprintResult } from 'fingerprinter-js'
import type {
  EventPayloads,
  EventType,
  EventTarget,
} from '@/schema/analytics/event'

type TrackerCache = {
  channel: BasedChannel<any>
  editionId: string
  close: () => void
}

type QueuedEvent = {
  event: EventType
  eventData?: any
}

const getPageTarget = (sequenceType?: string): EventTarget => {
  const path = window.location.pathname
  if (path.includes('/checkout')) return 'page:checkout'
  if (path.includes('/cart')) return 'page:cart'
  if (path.includes('/thank-you')) return 'page:thank-you'
  if (path.includes('/terms')) return 'page:terms'
  if (path.includes('/privacy')) return 'page:privacy'
  if (path.includes('/imprint')) return 'page:imprint'
  if (sequenceType === 'voting') return 'page:voting'
  return 'page:home'
}

type UseTrackOptions = {
  editionId: string
  sequenceType?: string
}

export const useTrack = ({ editionId, sequenceType }: UseTrackOptions) => {
  const sequenceTypeRef = useRef(sequenceType)
  const client = useClient()
  const trackerRef = useRef<TrackerCache | null>(null)
  const eventQueueRef = useRef<QueuedEvent[]>([])
  const lastEventTimeByPrefixRef = useRef<Map<string, number>>(new Map())
  const hasFiredDeloadRef = useRef<boolean>(false)
  const fpRef = useRef<{
    hasFingerPrint: boolean
    promise: Promise<FingerprintResult>
    fp?: string
  }>({
    hasFingerPrint: false,
    promise: Fingerprint.generate(),
  })

  const getTargetPrefix = (target?: string): string => {
    if (!target) return '_default'
    const colonIndex = target.indexOf(':')
    if (colonIndex === -1) return target
    return target.slice(0, colonIndex)
  }

  const publishEvent = (
    event: EventType,
    eventData: any,
    fingerprint: string,
  ) => {
    const now = Date.now()
    const prefix = getTargetPrefix(eventData?.target)
    const lastTime = lastEventTimeByPrefixRef.current.get(prefix) ?? now
    const delta = now - lastTime
    lastEventTimeByPrefixRef.current.set(prefix, now)
    trackerRef.current?.channel.publish({
      event,
      eventData,
      fingerprint,
      delta,
    })
  }

  sequenceTypeRef.current = sequenceType

  const fireDeload = () => {
    if (hasFiredDeloadRef.current) return
    hasFiredDeloadRef.current = true
    const fp = fpRef.current.fp
    if (fp) {
      publishEvent(
        'page-deload',
        { target: getPageTarget(sequenceTypeRef.current) },
        fp,
      )
    }
  }

  const onEventRef = useRef(
    <T extends EventType>(event: T, eventData?: EventPayloads[T]) => {
      let data = eventData
      if (event === 'page-load' && data && !('target' in data && data.target)) {
        data = {
          ...data,
          target: getPageTarget(sequenceTypeRef.current),
        } as EventPayloads[T]
      }
      if (!fpRef.current?.hasFingerPrint) {
        eventQueueRef.current.push({ event, eventData: data })
      } else {
        publishEvent(event, data, fpRef.current.fp!)
      }
    },
  )

  useEffect(() => {
    if (trackerRef.current?.editionId !== editionId) {
      if (trackerRef.current) {
        trackerRef.current.close()
      }
      const channel = client.channel('track', { editionId })
      const close = channel.subscribe(() => {})
      trackerRef.current = {
        channel,
        editionId,
        close,
      }
    }

    fpRef.current.promise
      .then((fp) => {
        fpRef.current.hasFingerPrint = true
        fpRef.current.fp = fp.fingerprint

        for (const queuedEvent of eventQueueRef.current) {
          publishEvent(queuedEvent.event, queuedEvent.eventData, fp.fingerprint)
        }
        eventQueueRef.current = []
      })
      .catch(() => {
        fpRef.current.hasFingerPrint = true
        fpRef.current.fp = 'unknown'

        for (const queuedEvent of eventQueueRef.current) {
          publishEvent(queuedEvent.event, queuedEvent.eventData, 'unknown')
        }
        eventQueueRef.current = []
      })

    const handleVisibilityChange = () => {
      if (document.hidden) {
        fireDeload()
      }
    }

    const handleBeforeUnload = () => {
      fireDeload()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      trackerRef.current?.close()
      trackerRef.current = null
    }
  }, [editionId])

  return onEventRef.current
}
